import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { useUpdateWorkerProfile } from "@/features/workers/model/useWorkerProfile";
import type { WorkerProfile } from "@/features/workers/types";
import { formatPhoneInput, nationalPhoneDigits } from "@/shared/lib/phone";

const schema = z.object({
  first_name: z.string().min(1, "Required").max(100),
  last_name: z.string().min(1, "Required").max(100),
  // Required + must be 10 digits after stripping. Backend re-validates and
  // converts to E.164 so SMS notifications (shift-confirmed, reminders) can
  // actually deliver.
  phone: z
    .string()
    .min(1, "Phone is required")
    .refine((v) => nationalPhoneDigits(v).length === 10, {
      message: "Enter a 10-digit phone number",
    }),
  city: z.string().min(1, "Required").max(100),
});

type Values = z.infer<typeof schema>;

/** Split a stored full name into (first, rest). Used to seed the edit forms
 *  so existing workers don't lose what they typed before the split. */
export function splitName(full: string): { first: string; last: string } {
  const trimmed = full.trim();
  const idx = trimmed.indexOf(" ");
  if (idx === -1) return { first: trimmed, last: "" };
  return { first: trimmed.slice(0, idx), last: trimmed.slice(idx + 1).trim() };
}

interface Props {
  profile: WorkerProfile;
  initialPhone: string | null;
  onSaved: () => void;
  onBack?: () => void;
}

export function IdentityStep({ profile, initialPhone, onSaved, onBack }: Props) {
  const update = useUpdateWorkerProfile();
  const seeded = splitName(profile.name ?? "");
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: seeded.first,
      last_name: seeded.last,
      phone: initialPhone ? formatPhoneInput(initialPhone) : "",
      city: profile.city ?? "",
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    const fullName = `${values.first_name.trim()} ${values.last_name.trim()}`.trim();
    await update.mutateAsync({
      name: fullName,
      phone: `+1${nationalPhoneDigits(values.phone)}`,
      city: values.city,
    });
    onSaved();
  });

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold tracking-display-tight text-copy">About you</h2>
        <p className="mt-1 text-sm text-copy-muted">
          The basics venues will see when you apply.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <label htmlFor="first_name" className="text-sm font-medium text-copy">
            First name
          </label>
          <input
            id="first_name"
            {...register("first_name")}
            placeholder="Jamie"
            autoComplete="given-name"
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {errors.first_name && (
            <p className="text-xs text-destructive">{errors.first_name.message}</p>
          )}
        </div>
        <div className="space-y-1">
          <label htmlFor="last_name" className="text-sm font-medium text-copy">
            Last name
          </label>
          <input
            id="last_name"
            {...register("last_name")}
            placeholder="Smith"
            autoComplete="family-name"
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {errors.last_name && (
            <p className="text-xs text-destructive">{errors.last_name.message}</p>
          )}
        </div>
      </div>

      <div className="space-y-1">
        <label htmlFor="phone" className="text-sm font-medium text-copy">
          Phone number
        </label>
        <Controller
          control={control}
          name="phone"
          render={({ field }) => (
            <input
              id="phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={field.value}
              onChange={(e) => field.onChange(formatPhoneInput(e.target.value))}
              onBlur={field.onBlur}
              placeholder="+1 (613) 555-1234"
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          )}
        />
        {errors.phone && <p className="text-xs text-destructive">{errors.phone.message}</p>}
        <p className="text-xs text-copy-muted">
          We send shift confirmations and reminders by SMS.
        </p>
      </div>

      <div className="space-y-1">
        <label htmlFor="city" className="text-sm font-medium text-copy">
          City
        </label>
        <input
          id="city"
          {...register("city")}
          placeholder="Ottawa"
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        {errors.city && <p className="text-xs text-destructive">{errors.city.message}</p>}
      </div>

      <StepFooter
        onBack={onBack}
        isSubmitting={isSubmitting || update.isPending}
        nextLabel="Next"
      />
    </form>
  );
}

interface FooterProps {
  onBack?: () => void;
  isSubmitting: boolean;
  nextLabel?: string;
  nextDisabled?: boolean;
}

export function StepFooter({
  onBack,
  isSubmitting,
  nextLabel = "Next",
  nextDisabled = false,
}: FooterProps) {
  return (
    <div className="flex items-center justify-between border-t border-border pt-4">
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="rounded-md border border-border bg-surface px-3 py-2 text-sm hover:bg-surface-sunken"
        >
          Back
        </button>
      ) : (
        <span />
      )}
      <button
        type="submit"
        disabled={isSubmitting || nextDisabled}
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
      >
        {isSubmitting ? "Saving…" : nextLabel}
      </button>
    </div>
  );
}
